import { escapeHtml } from "../../../../utils/htmlUtils";
import { formatCurrency } from "./money";

export const QUOTATION_PLATFORM_NAME = "Sohuutritue.vn";
export const QUOTATION_PLATFORM_LABEL = `${QUOTATION_PLATFORM_NAME}`;
export const QUOTATION_PLATFORM_URL = "https://sohuutritue.vn/";

const normalizeText = (value) => String(value ?? "").trim();

export const getVatAmount = (subtotal, vat) =>
  Math.round((subtotal * (Number(vat) || 0)) / 100);

const renderInfoRow = (label, value, { keepHtml = false } = {}) => {
  const normalizedValue = normalizeText(value);

  return `
    <div class="info-row">
      <span class="info-label">${escapeHtml(label)}</span>
      <span class="info-value">${keepHtml ? value : escapeHtml(normalizedValue)}</span>
    </div>
  `;
};

const renderTotalCell = (amount, vatAmount) => `
  <td class="text-right">
    <div>${formatCurrency(amount)}</div>
    ${
      vatAmount > 0
        ? `<div class="vat-note">VAT: ${formatCurrency(vatAmount)}</div>`
        : ""
    }
  </td>
`;

const getServiceName = (service) =>
  normalizeText(service.customName) || normalizeText(service.name);

const getTierName = (service, tier, tierIndex) => {
  const customName = service.customTierNames?.[tierIndex];
  if (normalizeText(customName)) return customName;

  const tierMinQty = Number(tier.minQty) || 0;
  const tierMaxQty = tier.maxQty ? Number(tier.maxQty) : null;
  return `${tierMinQty} - ${tierMaxQty || "vô hạn"} ${getServiceName(service)}`;
};

const getTierValue = (serviceValue, fallbackValue) =>
  serviceValue !== undefined
    ? Number(serviceValue) || 0
    : Number(fallbackValue) || 0;

const buildServiceRows = ({ selectedServices, visibleTiers }) => {
  return selectedServices
    .map((service, index) => {
      const price = Number(service.displayPrice) || 0;
      const quantity = Number(service.quantity) || 1;
      const licenseFee = Number(service.licenseFee) || 0;
      const subtotalMain = (price + licenseFee) * quantity;
      const vatMain = getVatAmount(subtotalMain, service.tierVats?.[0]);
      const rows = [
        `
          <tr class="service-main-row">
            <td class="text-center">${index + 1}</td>
            <td>${escapeHtml(getServiceName(service))}</td>
            <td class="text-center">${quantity}</td>
            <td class="text-right">${formatCurrency(price)}</td>
            <td class="text-right">${formatCurrency(licenseFee)}</td>
            <td class="text-center">${Number(service.tierVats?.[0]) || 0}%</td>
            ${renderTotalCell(subtotalMain + vatMain, vatMain)}
          </tr>
        `,
      ];

      const otherTiers = (visibleTiers?.[service.id] || [0]).filter(
        (tierIndex) => tierIndex !== 0,
      );

      otherTiers.forEach((tierIndex) => {
        const tier = service.priceTiers?.[tierIndex];
        if (!tier) return;

        const tierQty =
          service.tierQuantities?.find((tq) => tq.tierIndex === tierIndex)
            ?.quantity || 1;
        const tierPrice = getTierValue(
          service.customTierPrices?.[tierIndex],
          tier.price,
        );
        const tierLicenseFee = getTierValue(
          service.customTierLicenseFees?.[tierIndex],
          tier.licenseFee || tier.certificateFee || tier.issueFee,
        );
        const tierSubtotal = (tierPrice + tierLicenseFee) * tierQty;
        const tierVat = getVatAmount(
          tierSubtotal,
          service.tierVats?.[tierIndex],
        );

        rows.push(`
          <tr class="service-detail-row">
            <td class="text-center"></td>
            <td>${escapeHtml(getTierName(service, tier, tierIndex))}</td>
            <td class="text-center">${tierQty}</td>
            <td class="text-right">${formatCurrency(tierPrice)}</td>
            <td class="text-right">${formatCurrency(tierLicenseFee)}</td>
            <td class="text-center">${Number(service.tierVats?.[tierIndex]) || 0}%</td>
            ${renderTotalCell(tierSubtotal + tierVat, tierVat)}
          </tr>
        `);
      });

      if (service.additionalFeeVisible) {
        const addFeePrice =
          service.additionalFeePrice !== undefined
            ? Number(service.additionalFeePrice)
            : Number(service.additionalFee) || 0;
        const addFeeQty =
          service.additionalFeeQuantity !== undefined
            ? Number(service.additionalFeeQuantity)
            : 1;
        const addFeeSubtotal = addFeePrice * addFeeQty;
        const addFeeVat = getVatAmount(
          addFeeSubtotal,
          service.additionalFeeVat,
        );

        if (addFeeSubtotal > 0) {
          rows.push(`
            <tr class="service-detail-row additional-fee-row">
              <td class="text-center"></td>
              <td>${escapeHtml(service.additionalFeeName || "Phí bổ sung")}</td>
              <td class="text-center">${addFeeQty}</td>
              <td class="text-right">${formatCurrency(addFeePrice)}</td>
              <td class="text-right">${formatCurrency(0)}</td>
              <td class="text-center">${Number(service.additionalFeeVat) || 0}%</td>
              ${renderTotalCell(addFeeSubtotal + addFeeVat, addFeeVat)}
            </tr>
          `);
        }
      }

      return rows.join("");
    })
    .join("");
};

const buildAdjustmentRows = (adjustments = []) =>
  adjustments
    .filter((adjustment) => normalizeText(adjustment?.reason))
    .map(
      (adjustment, index) => `
        <tr>
          <td class="adjustment-index-col text-center">${index + 1}</td>
          <td>${escapeHtml(normalizeText(adjustment.reason))}</td>
          <td class="text-center">${escapeHtml(normalizeText(adjustment.type))}</td>
          <td class="text-right">${formatCurrency(adjustment.amount)}</td>
        </tr>
      `,
    )
    .join("");

export const buildQuotationBodyHTML = ({
  date,
  specialist,
  email,
  phone,
  selectedServices,
  visibleTiers,
  adjustments = [],
  subtotal,
  vatTotal,
  grandTotal,
}) => {
  const platformHtml = `<a href="${QUOTATION_PLATFORM_URL}" class="info-link">${QUOTATION_PLATFORM_LABEL}</a>`;

  return `
    <h1 class="heading">BÁO GIÁ DỊCH VỤ</h1>

    <div class="info-section">
      ${renderInfoRow("Ngày:", date)}
      ${renderInfoRow("Chuyên viên:", specialist)}
      ${renderInfoRow("Email:", email)}
      ${renderInfoRow("Số điện thoại:", phone)}
      ${renderInfoRow("Nền tảng:", platformHtml, {
        keepHtml: true,
      })}
    </div>

    <table>
      <thead>
        <tr>
          <th class="col-index text-center">STT</th>
          <th>Tên dịch vụ</th>
          <th class="col-quantity text-center">SL</th>
          <th class="col-money text-right">Phí đăng ký</th>
          <th class="col-money text-right">Phí cấp bằng</th>
          <th class="col-vat text-center">VAT</th>
          <th class="col-money text-right">Thành tiền</th>
        </tr>
      </thead>
      <tbody>
        ${buildServiceRows({ selectedServices, visibleTiers })}
      </tbody>
    </table>

    ${
      adjustments.length > 0
        ? `
          <div class="adjustment-section">
            <div class="adjustment-title">Thông tin điều chỉnh</div>
            <table class="adjustment-table">
              <thead>
                <tr>
                  <th class="adjustment-index-col text-center">STT</th>
                  <th>Nội dung điều chỉnh</th>
                  <th class="adjustment-type-col">Loại điều chỉnh</th>
                  <th class="adjustment-amount-col">Số tiền</th>
                </tr>
              </thead>
              <tbody>
                ${buildAdjustmentRows(adjustments)}
              </tbody>
            </table>
          </div>
        `
        : ""
    }

    <div class="summary-section">
      <div class="summary-row">
        <span class="summary-label">Tạm tính:</span>
        <span class="summary-value">${formatCurrency(subtotal)}</span>
      </div>
      ${
        vatTotal > 0
          ? `<div class="summary-row"><span class="summary-label">Tổng VAT:</span><span class="summary-value">${formatCurrency(vatTotal)}</span></div>`
          : ""
      }
      <div class="total-row">
        <span class="total-label">TỔNG CỘNG:</span>
        <span class="total-amount">${formatCurrency(grandTotal)}</span>
      </div>
    </div>
  `;
};
